import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Upload, Download, AlertCircle } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { services } from '@/lib/api';
import { DEFAULT_AVATAR } from '@/constants';
import type { CreateTeacherDTO } from '@/interface/types/user';
import { useTranslation } from 'react-i18next';

interface TeacherImportData {
  firstname: string;
  lastname: string;
  email: string;
  cin: string;
  password: string;
  title: string;
  gender: string;
  birthday?: Date;
  address?: string;
  phone?: string;
  subjectNames?: string;
}

interface ImportResult {
  teacher: TeacherImportData;
  success: boolean;
  error?: string;
  rowNumber: number;
}

interface BulkImportStats {
  total: number;
  successful: number;
  failed: number;
  results: ImportResult[];
}

interface BulkTeacherImportProps {
  onImportComplete?: () => void;
  subjects: Array<{ id: string; name: string }>;
}

export function BulkTeacherImport({ onImportComplete, subjects }: BulkTeacherImportProps) {
  const { t } = useTranslation('admin');
  const [isOpen, setIsOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importStats, setImportStats] = useState<BulkImportStats | null>(null);
  const [showFailedDetails, setShowFailedDetails] = useState(false);
  const { toast } = useToast();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'text/csv') {
      setFile(selectedFile);
      setImportStats(null);
    } else {
      toast({
        title: t('teacher-folder.bulkImportTeacher.notifications.error.title'),
        description: t('teacher-folder.bulkImportTeacher.notifications.error.description'),
        variant: 'destructive'
      });
    }
  };

  const parseCSV = (csvText: string): TeacherImportData[] => {
    const lines = csvText.split('\n').filter(line => line.trim());
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    
    return lines.slice(1).map(line => {
      const values = line.split(',').map(v => v.trim());
      const teacher: any = {};
      
      headers.forEach((header, index) => {
        const value = values[index] || '';
        switch (header) {
          case 'firstname':
          case 'first name':
            teacher.firstname = value;
            break;
          case 'lastname':
          case 'last name':
            teacher.lastname = value;
            break;
          case 'email':
            teacher.email = value;
            break;
          case 'cin':
            teacher.cin = value;
            break;
          case 'password':
            teacher.password = value;
            break;
          case 'title':
            teacher.title = value;
            break;
          case 'gender':
            teacher.gender = value;
            break;
          case 'phone':
            teacher.phone = value;
            break;
          case 'address':
            teacher.address = value;
            break;
          case 'subjects':
          case 'subject names':
            teacher.subjectNames = value;
            break;
          case 'birthday':
          case 'birthdate':
            if (value) {
              const dateParts = value.split('/');
              if (dateParts.length === 3) {
                const day = parseInt(dateParts[0]);
                const month = parseInt(dateParts[1]) - 1;
                const year = parseInt(dateParts[2]);
                teacher.birthday = new Date(year, month, day);
              }
            }
            break;
        }
      });
      
      return teacher as TeacherImportData;
    });
  };

  const validateTeacher = (teacher: TeacherImportData): string | null => {
    if (!teacher.firstname || teacher.firstname.length < 2) {
      return t('teacher-folder.bulkImportTeacher.validation.firstName');
    }
    if (!teacher.lastname || teacher.lastname.length < 2) {
      return t('teacher-folder.bulkImportTeacher.validation.lastName');
    }
    if (!teacher.email || !/\S+@\S+\.\S+/.test(teacher.email)) {
      return t('teacher-folder.bulkImportTeacher.validation.email');
    }
    if (!teacher.cin || teacher.cin.length !== 8 || !/^[0-9]{8}$/.test(teacher.cin)) {
      return t('teacher-folder.bulkImportTeacher.validation.cin') || 'CIN must be exactly 8 digits (numbers only)';
    }
    if (!teacher.password || teacher.password.length < 6) {
      return t('teacher-folder.bulkImportTeacher.validation.password');
    }
    if (!teacher.title) {
      return t('teacher-folder.bulkImportTeacher.validation.title');
    }
    if (!teacher.gender || !['male', 'female'].includes(teacher.gender.toLowerCase())) {
      return t('teacher-folder.bulkImportTeacher.validation.gender');
    }
    
    if (teacher.birthday) {
      const today = new Date();
      const birthDate = new Date(teacher.birthday);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      if (age < 18) {
        return t('teacher-folder.bulkImportTeacher.validation.age');
      }
    }
    
    return null;
  };

  const getSubjectIds = (subjectNames: string): string[] => {
    if (!subjectNames) return [];
    
    const names = subjectNames.split(',').map(name => name.trim().toLowerCase());
    const subjectIds: string[] = [];
    
    names.forEach(name => {
      const subject = subjects.find(s => s.name.toLowerCase() === name);
      if (subject) {
        subjectIds.push(subject.id);
      }
    });
    
    return subjectIds;
  };

  const handleImport = async () => {
    if (!file) return;

    setImporting(true);
    setProgress(0);
    setImportStats(null);

    try {
      const csvText = await file.text();
      const teachers = parseCSV(csvText);
      const results: ImportResult[] = [];
      
      for (let i = 0; i < teachers.length; i++) {
        const teacher = teachers[i];
        const rowNumber = i + 2;
        
        const validationError = validateTeacher(teacher);
        if (validationError) {
          results.push({
            teacher,
            success: false,
            error: validationError,
            rowNumber
          });
          setProgress(((i + 1) / teachers.length) * 100);
          continue;
        }

        try {
          const defaultBirthday = new Date();
          defaultBirthday.setFullYear(defaultBirthday.getFullYear() - 30);

          const subjectIds = getSubjectIds(teacher.subjectNames || '');

          const createData: CreateTeacherDTO = {
            firstname: teacher.firstname,
            lastname: teacher.lastname,
            email: teacher.email,
            password: teacher.password,
            cin: teacher.cin,
            title: teacher.title,
            birthday: teacher.birthday ? teacher.birthday.toISOString() : defaultBirthday.toISOString(),
            gender: teacher.gender.toLowerCase() as 'male' | 'female',
            avatar: DEFAULT_AVATAR,
            isActive: true,
            subjectIds: subjectIds,
            address: teacher.address,
            phone: teacher.phone,
          };

          await services.teacher.create(createData);
          results.push({
            teacher,
            success: true,
            rowNumber
          });
        } catch (error: any) {
          let errorMessage = t('teacher-folder.bulkImportTeacher.validation.unknownError');
          
          if (error.response?.data) {
            const apiError = error.response.data;
            
            if (typeof apiError.message === 'string') {
              errorMessage = apiError.message;
            } else if (Array.isArray(apiError.message)) {
              errorMessage = apiError.message.map((err: any) => {
                if (typeof err === 'string') return err;
                if (err.field && err.message) {
                  if (err.field === 'email') return t('teacher-folder.bulkImportTeacher.validation.emailExists');
                  if (err.field === 'cin') return t('teacher-folder.bulkImportTeacher.validation.cinExists');
                  if (err.field === 'birthday') return t('teacher-folder.bulkImportTeacher.validation.invalidBirthday');
                  return `${err.field}: ${err.message}`;
                }
                return JSON.stringify(err);
              }).join(', ');
            } else if (apiError.error === 'CONFLICT') {
              errorMessage = t('teacher-folder.bulkImportTeacher.validation.dataConflict');
            } else if (apiError.error === 'BAD_REQUEST') {
              errorMessage = t('teacher-folder.bulkImportTeacher.validation.invalidData');
            } else {
              errorMessage = apiError.error || t('teacher-folder.bulkImportTeacher.validation.serverError');
            }
          } else if (error.message) {
            errorMessage = error.message;
          }
          
          results.push({
            teacher,
            success: false,
            error: errorMessage,
            rowNumber
          });
        }
        
        setProgress(((i + 1) / teachers.length) * 100);
      }

      const stats: BulkImportStats = {
        total: teachers.length,
        successful: results.filter(r => r.success).length,
        failed: results.filter(r => !r.success).length,
        results
      };

      setImportStats(stats);
      
      toast({
        title: t('teacher-folder.bulkImportTeacher.notifications.success.title'),
        description: t('teacher-folder.bulkImportTeacher.notifications.success.description', {
          successCount: stats.successful,
          totalCount: stats.total
        })
      });

      if (onImportComplete && stats.successful > 0) {
        onImportComplete();
      }

    } catch (error) {
      toast({
        title: t('teacher-folder.bulkImportTeacher.notifications.error.title'),
        description: t('teacher-folder.bulkImportTeacher.notifications.error.description'),
        variant: 'destructive'
      });
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const subjectNames = subjects.slice(0, 3).map(s => s.name).join(',');
    const template = `firstname,lastname,email,cin,password,title,gender,phone,address,subjects,birthday\nJohn,Smith,john.smith@example.com,12345678,Password123!,Professor,Male,+1234567890,123 Main St,"${subjectNames}",15/05/1985`;
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'teacher_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Button onClick={() => setIsOpen(true)} className="gap-2">
        <Upload className="h-4 w-4" />
        {t('teacher-folder.bulkImportTeacher.button')}
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('teacher-folder.bulkImportTeacher.modal.title')}</DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {t('teacher-folder.bulkImportTeacher.modal.templateAlert.description')}
                <Button variant="link" onClick={downloadTemplate} className="p-0 ml-2">
                  <Download className="h-4 w-4 mr-1" />
                  {t('teacher-folder.bulkImportTeacher.modal.templateAlert.downloadButton')}
                </Button>
              </AlertDescription>
            </Alert>

            <div className="p-4 bg-blue-50 rounded-lg">
              <h4 className="font-medium mb-2">
                {t('teacher-folder.bulkImportTeacher.modal.subjectsInfo.title')}
              </h4>
              <div className="flex flex-wrap gap-1">
                {subjects.map(subject => (
                  <Badge key={subject.id} variant="secondary" className="text-xs">
                    {subject.name}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="csv-file">
                {t('teacher-folder.bulkImportTeacher.modal.fileUpload.label')}
              </Label>
              <Input
                id="csv-file"
                type="file"
                accept=".csv"
                onChange={handleFileChange}
                disabled={importing}
              />
            </div>

            {importing && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>{t('teacher-folder.bulkImportTeacher.modal.importProgress.text')}</span>
                  <span>
                    {t('teacher-folder.bulkImportTeacher.modal.importProgress.percentage', {
                      progress: Math.round(progress)
                    })}
                  </span>
                </div>
                <Progress value={progress} />
              </div>
            )}

            {importStats && (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">
                      {importStats.total}
                    </div>
                    <div className="text-sm text-blue-600">
                      {t('teacher-folder.bulkImportTeacher.modal.results.total.title')}
                    </div>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">
                      {importStats.successful}
                    </div>
                    <div className="text-sm text-green-600">
                      {t('teacher-folder.bulkImportTeacher.modal.results.successful.title')}
                    </div>
                  </div>
                  <div className="text-center p-4 bg-red-50 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">
                      {importStats.failed}
                    </div>
                    <div className="text-sm text-red-600">
                      {t('teacher-folder.bulkImportTeacher.modal.results.failed.title')}
                    </div>
                  </div>
                </div>

                {importStats.failed > 0 && (
                  <Button 
                    variant="outline" 
                    onClick={() => setShowFailedDetails(!showFailedDetails)}
                    className="w-full"
                  >
                    {showFailedDetails 
                      ? t('teacher-folder.bulkImportTeacher.modal.results.showDetails.hide')
                      : t('teacher-folder.bulkImportTeacher.modal.results.showDetails.show')}
                  </Button>
                )}

                {showFailedDetails && importStats.failed > 0 && (
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold mb-3">
                      {t('teacher-folder.bulkImportTeacher.modal.results.failedTable.title', {
                        count: importStats.failed
                      })}
                    </h4>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>
                            {t('teacher-folder.bulkImportTeacher.modal.results.failedTable.headers.row')}
                          </TableHead>
                          <TableHead>
                            {t('teacher-folder.bulkImportTeacher.modal.results.failedTable.headers.name')}
                          </TableHead>
                          <TableHead>
                            {t('teacher-folder.bulkImportTeacher.modal.results.failedTable.headers.email')}
                          </TableHead>
                          <TableHead>
                            {t('teacher-folder.bulkImportTeacher.modal.results.failedTable.headers.cin')}
                          </TableHead>
                          <TableHead>
                            {t('teacher-folder.bulkImportTeacher.modal.results.failedTable.headers.error')}
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {importStats.results
                          .filter(result => !result.success)
                          .map((result, index) => (
                            <TableRow key={index}>
                              <TableCell>{result.rowNumber}</TableCell>
                              <TableCell>
                                {result.teacher.firstname} {result.teacher.lastname}
                              </TableCell>
                              <TableCell>{result.teacher.email}</TableCell>
                              <TableCell>{result.teacher.cin}</TableCell>
                              <TableCell>
                                <Badge variant="destructive" className="text-xs">
                                  {result.error}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsOpen(false)}>
                {t('teacher-folder.bulkImportTeacher.modal.buttons.close')}
              </Button>
              <Button 
                onClick={handleImport} 
                disabled={!file || importing}
                className="gap-2"
              >
                {importing ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    {t('teacher-folder.bulkImportTeacher.modal.buttons.import.importing')}
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4" />
                    {t('teacher-folder.bulkImportTeacher.modal.buttons.import.default')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}